import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Download, ExternalLink, Loader2, RotateCw } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { FiscalInvoiceStatusBadge } from '@/components/fiscal/FiscalStatusBadge';
import { FiscalEventTimeline } from '@/components/fiscal/FiscalEventTimeline';
import { CopyValueButton } from '@/components/shared/CopyValueButton';
import { formatPLN } from '@/utils/currency';
import { computeFiscalInvoiceGross } from '@/utils/fiscalAmounts';
import { downloadBlob } from '@/utils/fiscalDownload';
import { useFiscalApi, type FiscalEventItem, type FiscalInvoiceListItem } from '@/services/api';
import { t } from '@/i18n';

const RETRYABLE_STATUSES = new Set(['REJECTED', 'RETRY_WAIT']);

interface FiscalInvoiceDetailDialogProps {
  nip: string;
  uuid: string | null;
  onOpenChange: (open: boolean) => void;
  /** Called after a successful retry so the caller's list refreshes. */
  onChanged: () => void;
}

export function FiscalInvoiceDetailDialog({ nip, uuid, onOpenChange, onChanged }: FiscalInvoiceDetailDialogProps) {
  const { getInvoice, getInvoiceEvents, getInvoiceXml, getInvoiceUpo, getInvoiceQrPng, retryInvoice } =
    useFiscalApi();
  const [invoice, setInvoice] = useState<FiscalInvoiceListItem | null>(null);
  const [events, setEvents] = useState<FiscalEventItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [qrUrl, setQrUrl] = useState<string | null>(null);
  const [xmlLoading, setXmlLoading] = useState(false);
  const [upoLoading, setUpoLoading] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [retryConfirmOpen, setRetryConfirmOpen] = useState(false);

  const load = useCallback(async () => {
    if (!uuid) return;
    setLoading(true);
    try {
      const [inv, ev] = await Promise.all([getInvoice(nip, uuid), getInvoiceEvents(nip, uuid)]);
      setInvoice(inv);
      setEvents(ev);
    } catch {
      toast.error(t('errors.loadingFailed'));
    } finally {
      setLoading(false);
    }
  }, [nip, uuid, getInvoice, getInvoiceEvents]);

  useEffect(() => {
    if (uuid) load();
    else {
      setInvoice(null);
      setEvents([]);
    }
  }, [uuid, load]);

  // QR preview — only ever populated for CONFIRMED invoices (backend 409s otherwise).
  useEffect(() => {
    if (!uuid || !invoice || invoice.status !== 'CONFIRMED') {
      setQrUrl(null);
      return;
    }
    let cancelled = false;
    let objectUrl: string | null = null;
    getInvoiceQrPng(nip, uuid)
      .then((blob) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setQrUrl(objectUrl);
      })
      .catch(() => {
        /* QR is a nice-to-have — silently skip if it fails to load */
      });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [nip, uuid, invoice, getInvoiceQrPng]);

  const handleDownloadXml = async () => {
    if (!uuid || !invoice) return;
    setXmlLoading(true);
    try {
      const blob = await getInvoiceXml(nip, uuid);
      downloadBlob(blob, `${invoice.number || uuid}.xml`);
    } catch {
      toast.error(t('fiscal.invoices.detail.downloadError'));
    } finally {
      setXmlLoading(false);
    }
  };

  const handleDownloadUpo = async () => {
    if (!uuid || !invoice) return;
    setUpoLoading(true);
    try {
      const blob = await getInvoiceUpo(nip, uuid);
      downloadBlob(blob, `UPO_${invoice.number || uuid}.xml`);
    } catch {
      toast.error(t('fiscal.invoices.detail.downloadError'));
    } finally {
      setUpoLoading(false);
    }
  };

  const handleRetry = async () => {
    if (!uuid) return;
    setRetrying(true);
    try {
      const result = await retryInvoice(nip, uuid);
      if (result.ok) {
        toast.success(t('fiscal.invoices.detail.retrySuccess'));
        onChanged();
        load();
      } else {
        toast.warning(t('fiscal.invoices.detail.retryConflict'));
      }
    } catch {
      toast.error(t('fiscal.invoices.detail.retryError'));
    } finally {
      setRetrying(false);
      setRetryConfirmOpen(false);
    }
  };

  const isConfirmed = invoice?.status === 'CONFIRMED';
  const canRetry = invoice ? RETRYABLE_STATUSES.has(invoice.status) : false;
  const gross = invoice ? computeFiscalInvoiceGross(invoice.invoice) : 0;

  return (
    <>
    <Dialog open={uuid !== null} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t('fiscal.invoices.detail.title', { number: invoice?.number ?? '…' })}</DialogTitle>
        </DialogHeader>

        {loading && !invoice ? (
          <div className="space-y-3">
            <Skeleton className="h-6 w-40" />
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-32 w-full" />
          </div>
        ) : invoice ? (
          <div className="flex flex-col gap-4">
            <div className="flex flex-wrap items-center gap-3">
              <FiscalInvoiceStatusBadge status={invoice.status} />
              {invoice.confirmedAt && (
                <span className="text-muted-foreground text-xs">
                  {new Date(invoice.confirmedAt).toLocaleString('pl-PL')}
                </span>
              )}
            </div>

            {invoice.status === 'REJECTED' && invoice.error && (
              <Alert variant="destructive">
                <AlertTitle>{t('fiscal.invoices.detail.error')}</AlertTitle>
                <AlertDescription>
                  <p>{invoice.error.message}</p>
                  <p className="text-xs">{t('fiscal.invoices.detail.attempts', { count: invoice.error.attempts })}</p>
                  {invoice.error.nextRetryAt && (
                    <p className="text-xs">
                      {t('fiscal.invoices.detail.nextRetryAt', {
                        date: new Date(invoice.error.nextRetryAt).toLocaleString('pl-PL'),
                      })}
                    </p>
                  )}
                </AlertDescription>
              </Alert>
            )}

            <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
              <div>
                <p className="text-muted-foreground text-xs">{t('fiscal.invoices.detail.buyer')}</p>
                <p>{invoice.invoice.buyer.name}</p>
              </div>
              <div>
                <p className="text-muted-foreground text-xs">{t('fiscal.invoices.detail.amount')}</p>
                <p className="font-mono tabular-nums">
                  {formatPLN(gross)} {invoice.invoice.currency}
                </p>
              </div>
              {invoice.ksefNumber && (
                <div className="col-span-2">
                  <p className="text-muted-foreground text-xs">{t('fiscal.invoices.detail.ksefNumber')}</p>
                  <p className="inline-flex items-center gap-1 font-mono text-sm break-all">
                    {invoice.ksefNumber}
                    <CopyValueButton value={invoice.ksefNumber} />
                  </p>
                </div>
              )}
            </div>

            {isConfirmed && (
              <>
                <Separator />
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
                  {qrUrl && (
                    <div className="flex flex-col items-center gap-1">
                      <p className="text-muted-foreground text-xs">{t('fiscal.invoices.detail.qrTitle')}</p>
                      <img src={qrUrl} alt={t('fiscal.invoices.detail.qrTitle')} className="size-32 rounded border bg-white p-1" />
                    </div>
                  )}
                  <div className="flex flex-1 flex-col gap-2">
                    {invoice.verificationUrl && (
                      <a
                        href={invoice.verificationUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="text-primary inline-flex items-center gap-1 text-sm underline-offset-2 hover:underline"
                      >
                        <ExternalLink className="size-3.5" />
                        {t('fiscal.invoices.detail.verificationLink')}
                      </a>
                    )}
                    <div className="flex flex-wrap gap-2">
                      <Button variant="outline" size="sm" disabled={xmlLoading} onClick={handleDownloadXml}>
                        {xmlLoading ? <Loader2 className="size-4 animate-spin" /> : <Download className="size-4" />}
                        {t('fiscal.invoices.detail.downloadXml')}
                      </Button>
                      <Button variant="outline" size="sm" disabled={upoLoading} onClick={handleDownloadUpo}>
                        {upoLoading ? <Loader2 className="size-4 animate-spin" /> : <Download className="size-4" />}
                        {t('fiscal.invoices.detail.downloadUpo')}
                      </Button>
                    </div>
                  </div>
                </div>
              </>
            )}

            <Separator />
            <FiscalEventTimeline
              events={events}
              title={t('fiscal.invoices.detail.eventsTitle')}
              emptyLabel={t('fiscal.invoices.detail.noEvents')}
            />

            {canRetry && (
              <div className="flex justify-end">
                <Button size="sm" onClick={() => setRetryConfirmOpen(true)}>
                  <RotateCw className="size-4" />
                  {t('fiscal.invoices.detail.retry')}
                </Button>
              </div>
            )}
          </div>
        ) : null}
      </DialogContent>
    </Dialog>

    <AlertDialog open={retryConfirmOpen} onOpenChange={setRetryConfirmOpen}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t('fiscal.invoices.detail.retryConfirmTitle')}</AlertDialogTitle>
          <AlertDialogDescription>
            {t('fiscal.invoices.detail.retryConfirmMessage', { number: invoice?.number ?? '' })}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={retrying}>{t('common.cancel')}</AlertDialogCancel>
          <AlertDialogAction disabled={retrying} onClick={handleRetry}>
            {retrying && <Loader2 className="size-4 animate-spin" />}
            {t('fiscal.invoices.detail.retry')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  );
}
